/**
 * PriorityChipStack — compact per-dispensary indicator showing which
 * Partners have flagged this account as a priority. Rendered inline on
 * the Dispensaries list row and inside the Dispensary Detail header
 * strip.
 *
 * Behavior:
 *   • Up to 3 chips visible; overflow collapses into a "+N" chip
 *   • Each chip shows the partner's 2-letter initials + brand color
 *   • Hover tooltip = partner name (and per-pairing notes, if detailed=true)
 *   • Clicking a chip navigates to `/partners?focus=<id>` so admins can
 *     jump to the partner's priority list manager
 *
 * This component accepts data in two shapes:
 *   1. `partners`: array of `{partner_id, partner_name, brand_color,
 *      partner_notes}` — used by Dispensary Detail (richer).
 *   2. `partner_ids` + `partnerMap`: used by the Dispensary list row
 *      where we have the denormalized `priority_partner_ids` and a
 *      cached id→partner map at page level, avoiding N+1 fetches.
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { partnerInitials } from "../lib/api";

const MAX_VISIBLE = 3;

function Chip({ partner, size = "sm", withTooltip = true, detailed = false }) {
  const navigate = useNavigate();
  const diameter = size === "md" ? 24 : 18;
  const fontSize = size === "md" ? 10 : 9;
  const color = partner.brand_color || "#64748b";
  const initials = partnerInitials(partner.partner_name || partner.name);
  const tooltip = detailed && partner.partner_notes
    ? `${partner.partner_name || partner.name} — ${partner.partner_notes}`
    : (partner.partner_name || partner.name || "");
  const pid = partner.partner_id || partner.id;
  return (
    <button
      type="button"
      onClick={(e) => {
        // Stop + prevent so the parent dispensary-row <Link> doesn't
        // swallow the click — we want to send the user to the Partner
        // drawer, not the dispensary detail.
        e.preventDefault();
        e.stopPropagation();
        navigate(`/partners?focus=${pid}`);
      }}
      data-testid={`priority-chip-${pid}`}
      title={withTooltip ? tooltip : undefined}
      aria-label={tooltip}
      className="inline-flex items-center justify-center font-mono font-bold rounded-full shadow-sm transition-transform hover:scale-110 hover:z-10 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400"
      style={{
        width: diameter,
        height: diameter,
        fontSize,
        background: color,
        color: "#0f172a",
        border: "2px solid var(--chip-ring, rgb(9, 9, 11))",
        marginLeft: size === "md" ? 0 : -4,
        padding: 0,
        cursor: "pointer",
      }}
    >
      {initials}
    </button>
  );
}

export function PriorityChipStack({
  partners,            // enriched array (preferred)
  partner_ids,         // denormalized id array (fallback)
  partnerMap,          // id → {name, brand_color} dictionary
  size = "sm",
  detailed = false,
  dataTestId = "priority-chip-stack",
}) {
  // Normalize to a single enriched array
  const enriched = React.useMemo(() => {
    if (Array.isArray(partners) && partners.length) return partners;
    if (Array.isArray(partner_ids) && partner_ids.length && partnerMap) {
      return partner_ids
        .map((id) => {
          const p = partnerMap[id];
          if (!p) return null;
          return {
            partner_id: id,
            partner_name: p.name,
            brand_color: p.brand_color,
          };
        })
        .filter(Boolean);
    }
    return [];
  }, [partners, partner_ids, partnerMap]);

  if (!enriched.length) return null;

  const visible = enriched.slice(0, MAX_VISIBLE);
  const overflow = enriched.length - visible.length;

  return (
    <div
      data-testid={dataTestId}
      className="inline-flex items-center"
      style={{ paddingLeft: size === "md" ? 0 : 4 }}
    >
      {visible.map((p) => (
        <Chip
          key={p.partner_id || p.id}
          partner={p}
          size={size}
          detailed={detailed}
        />
      ))}
      {overflow > 0 && (
        <span
          data-testid="priority-chip-overflow"
          className="inline-flex items-center justify-center font-mono font-bold rounded-full bg-zinc-700 text-zinc-100 shadow-sm"
          style={{
            width: size === "md" ? 24 : 18,
            height: size === "md" ? 24 : 18,
            fontSize: size === "md" ? 10 : 9,
            border: "2px solid var(--chip-ring, rgb(9, 9, 11))",
            marginLeft: size === "md" ? 0 : -4,
          }}
          title={`+${overflow} more`}
        >
          +{overflow}
        </span>
      )}
    </div>
  );
}

export default PriorityChipStack;
